import { useEffect, useRef, useState } from 'react';
import { alpha, TextField, Typography, useTheme } from '@mui/material';
import { SGTooltip } from 'components/core';

const NAME_MAX = 40;

interface InlineEditableNameProps {
  value: string;
  onCommit: (name: string) => void;
}

/**
 * Click-to-edit group name: a label that swaps to a text field on click.
 * Commits on blur / Enter (when non-empty and changed), reverts on Escape.
 */
export const InlineEditableName = ({
  value,
  onCommit,
}: InlineEditableNameProps) => {
  const theme = useTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);

  // Keep the draft aligned with the latest value while not editing.
  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  // Commit runs once, on blur — Enter/Escape just blur the input.
  const finish = () => {
    if (cancelRef.current) {
      cancelRef.current = false;
      setDraft(value);
      setEditing(false);
      return;
    }
    const trimmed = draft.trim().slice(0, NAME_MAX);
    if (trimmed.length > 0 && trimmed !== value) {
      onCommit(trimmed);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        autoFocus
        variant="standard"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelRef.current = true;
            inputRef.current?.blur();
          }
        }}
        inputProps={{ maxLength: NAME_MAX }}
        sx={{
          '& .MuiInput-input': {
            fontSize: 13,
            fontWeight: 600,
            py: 0,
            width: 130,
          },
        }}
      />
    );
  }

  return (
    <SGTooltip title="Click to rename" placement="top">
      <Typography
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: theme.palette.text.primary,
          cursor: 'text',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          borderRadius: 0.5,
          px: 1,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {value}
      </Typography>
    </SGTooltip>
  );
};

export default InlineEditableName;
